import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Avatar, Badge, Button, Card, Divider, Input, List,
    Modal, Select, Skeleton, Space, Tag, Timeline, Tooltip, Typography,
} from "antd";
import {
    BankOutlined, CarOutlined, CheckCircleOutlined, ClockCircleOutlined,
    CommentOutlined, DollarOutlined, FileTextOutlined, InfoCircleOutlined,
    MessageOutlined, PaperClipOutlined, PlusOutlined, SendOutlined,
    UserOutlined, WarningOutlined,
} from "@ant-design/icons";
import { Footer } from "antd/es/layout/layout";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { LOGGED_USER } from "../../../variables/constants";
import { frenchDate, frenchDateOnly } from "../../../variables/functions";
import { searchSinistre } from "../../../hooks/useSearch";
import { highlightMatch } from "../../../variables/stringFunctions";
import axiosInstance from "../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../variables/numbersFunctions";

const { TextArea } = Input;
const { Text, Title } = Typography;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUTS = {
    A:  { label: "En attente",          color: "gold",    bg: "#fffbe6", border: "#faad14" },
    I:  { label: "En instruction",      color: "blue",    bg: "#e6f4ff", border: "#1677ff" },
    T:  { label: "Clôturé",             color: "green",   bg: "#f6ffed", border: "#52c41a" },
    S:  { label: "Suspendu",            color: "red",     bg: "#fff2f0", border: "#ff4d4f" },
    RF: { label: "Refusé",              color: "volcano", bg: "#fff2e8", border: "#fa541c" },
    R:  { label: "En règlement",        color: "purple",  bg: "#f9f0ff", border: "#722ed1" },
};

const getStatut = (s) => STATUTS[s] || STATUTS.I;

const resolveNumeroSinistre = (s) =>
    s?.numSinistre || s?.numeroSinistreM || s?.numeroSinistreP || s?.codeId || "—";

const initials = (nom, prenom) => {
    const a = (nom || "?")[0].toUpperCase();
    const b = (prenom || "")[0]?.toUpperCase() || "";
    return a + b;
};

const enginLabel = (engin) => {
    if (!engin) return "—";
    return [engin.immatriculation, engin.marque].filter(Boolean).join(" · ");
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

const SinistreHeaderBand = ({ sinistre, loading }) => {
    if (loading || !sinistre) return <Skeleton active paragraph={{ rows: 1 }} />;
    const statut = getStatut(sinistre?.sortDossier);
    const engin = sinistre?.engin ?? sinistre?.contrat?.engins?.[0];
    return (
        <div
            className="card mb-4"
            style={{
                borderLeft: `4px solid ${statut.border}`,
                background: statut.bg,
                borderRadius: 8,
            }}
        >
            <div className="card-body py-3">
                <div className="d-flex flex-wrap align-items-center gap-3 justify-content-between">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <Tag
                            color={statut.color}
                            style={{ fontSize: 13, padding: "3px 10px", fontWeight: 600 }}
                        >
                            {statut.label}
                        </Tag>
                        <div>
                            <Text type="secondary" style={{ fontSize: 11 }}>N° SINISTRE</Text>
                            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                                {resolveNumeroSinistre(sinistre)}
                            </div>
                        </div>
                        <Divider type="vertical" style={{ height: 32 }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 11 }}>POLICE</Text>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {sinistre?.contrat?.numeroPoliceM || sinistre?.contrat?.numeroPoliceP || "—"}
                            </div>
                        </div>
                        <Divider type="vertical" style={{ height: 32 }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 11 }}>VÉHICULE</Text>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {enginLabel(engin)}
                            </div>
                        </div>
                        <Divider type="vertical" style={{ height: 32 }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 11 }}>DATE SINISTRE</Text>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {frenchDateOnly(sinistre?.dateSinistre)}
                            </div>
                        </div>
                        <Divider type="vertical" style={{ height: 32 }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 11 }}>COMPAGNIE</Text>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {sinistre?.contrat?.organisme?.raisonSociale || "—"}
                            </div>
                        </div>
                    </div>
                    {sinistre?.typeSinistre && (
                        <Tag color={sinistre.typeSinistre === "CORPOREL" ? "red" : "orange"}>
                            {sinistre.typeSinistre}
                        </Tag>
                    )}
                </div>
            </div>
        </div>
    );
};

const InfoRow = ({ label, value, icon }) => (
    <div className="d-flex justify-content-between align-items-start py-1" style={{ borderBottom: "1px solid #f0f0f0" }}>
        <Text type="secondary" style={{ fontSize: 12, minWidth: 140 }}>
            {icon && <span className="me-1">{icon}</span>}
            {label}
        </Text>
        <Text style={{ fontSize: 13, fontWeight: 500, textAlign: "right", maxWidth: 220, wordBreak: "break-word" }}>
            {value ?? "—"}
        </Text>
    </div>
);

const MontantBadge = ({ label, montant, color = "blue", icon }) => (
    <div
        className="text-center p-3"
        style={{ borderRadius: 8, border: `1px solid`, borderColor: color === "green" ? "#b7eb8f" : color === "red" ? "#ffa39e" : "#91caff", background: color === "green" ? "#f6ffed" : color === "red" ? "#fff2f0" : "#e6f4ff" }}
    >
        {icon && <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>}
        <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: color === "green" ? "#52c41a" : color === "red" ? "#ff4d4f" : "#1677ff" }}>
            {displayNumbers(montant || 0)} FCFA
        </div>
    </div>
);

// ─── Onglet Informations ───────────────────────────────────────────────────────

const TabInfos = ({ sinistre, montant, montantPaye }) => {
    const engin = sinistre?.engin ?? sinistre?.contrat?.engins?.[0];
    return (
        <div className="row g-3">
            {/* Véhicule */}
            <div className="col-md-6">
                <Card
                    size="small"
                    title={<span><CarOutlined className="me-2 text-primary" />Véhicule assuré</span>}
                    className="h-100"
                >
                    <InfoRow label="Immatriculation" value={<b>{engin?.immatriculation}</b>} />
                    <InfoRow label="N° Chassis" value={engin?.numeroChassis} />
                    <InfoRow label="Marque" value={engin?.marque} />
                    <InfoRow label="Catégorie" value={engin?.categorie?.nom?.toUpperCase()} />
                    <InfoRow label="Nb places" value={engin?.nombrePlace} />
                    <InfoRow label="Énergie" value={engin?.energie} />
                    <InfoRow label="Valeur vénale" value={engin?.valeurVenale ? `${displayNumbers(engin.valeurVenale)} FCFA` : null} />
                </Card>
            </div>

            {/* Déclaration */}
            <div className="col-md-6">
                <Card
                    size="small"
                    title={<span><FileTextOutlined className="me-2 text-warning" />Déclaration</span>}
                    className="h-100"
                >
                    <InfoRow label="Date sinistre" value={frenchDate(sinistre?.dateSinistre)} />
                    <InfoRow label="Date déclaration" value={frenchDate(sinistre?.dateDeclaration)} />
                    <InfoRow label="Lieu" value={sinistre?.lieuSinistre} />
                    <InfoRow label="Circonstances" value={sinistre?.circonstances} />
                    <InfoRow label="Cause" value={sinistre?.causeSinistre} />
                    <InfoRow label="Type" value={sinistre?.typeSinistre} />
                    <InfoRow label="Entité constat" value={sinistre?.entiteConstat?.nom} />
                </Card>
            </div>

            {/* Assuré */}
            <div className="col-md-6">
                <Card
                    size="small"
                    title={<span><UserOutlined className="me-2 text-success" />Assuré / Souscripteur</span>}
                >
                    <InfoRow
                        label="Nom complet"
                        value={[sinistre?.contrat?.souscripteur?.nom, sinistre?.contrat?.souscripteur?.prenom].filter(Boolean).join(" ")}
                    />
                    <InfoRow label="Téléphone" value={sinistre?.contrat?.souscripteur?.telephone} />
                    <InfoRow label="Adresse" value={sinistre?.contrat?.souscripteur?.adresse} />
                </Card>
            </div>

            {/* Finances résumé */}
            <div className="col-md-6">
                <Card size="small" title={<span><DollarOutlined className="me-2 text-danger" />Situation financière</span>}>
                    <div className="row g-2">
                        <div className="col-6">
                            <MontantBadge label="Provision totale" montant={montant} color="blue" />
                        </div>
                        <div className="col-6">
                            <MontantBadge label="Décaissé" montant={montantPaye} color="green" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <InfoRow label="Police" value={sinistre?.contrat?.numeroPoliceM || sinistre?.contrat?.numeroPoliceP} />
                        <InfoRow label="Effet" value={frenchDate(sinistre?.contrat?.dateEffet)} />
                        <InfoRow label="Échéance" value={frenchDate(sinistre?.contrat?.dateEcheance)} />
                    </div>
                </Card>
            </div>
        </div>
    );
};

// ─── Onglet Recours ────────────────────────────────────────────────────────────

const SOURCE_RECOURS = [
    { value: "COMPAGNIE_ADVERSE", label: "Compagnie adverse" },
    { value: "BUREAU_NATIONAL_CARTE_BRUNE", label: "Bureau National Carte Brune" },
    { value: "AUTRE", label: "Autre" },
];

const TabRecours = ({ sinistre, encaissements, recoursEvaluations, setRefetch, loadingSinistre }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);
    const [form, setForm] = useState({
        montant: "",
        source: "COMPAGNIE_ADVERSE",
        sourceNom: "",
        dateEncaissement: "",
        reference: "",
        observations: "",
    });

    const totalRecours = encaissements?.reduce((acc, e) => acc + (parseFloat(e.montantGlobal) || 0), 0) || 0;

    const handleSave = () => {
        if (!form.montant || !form.source) {
            toast.warning("Montant et source sont obligatoires");
            return;
        }
        setLoadingSave(true);
        axiosInstance
            .post(`/api/sinistre/${sinistre?.codeId}/recours`, {
                ...form,
                sinistre: { codeId: sinistre?.codeId },
                encaissement: true,
            })
            .then(() => {
                toast.success("Encaissement recours enregistré");
                setModalOpen(false);
                setForm({ montant: "", source: "COMPAGNIE_ADVERSE", sourceNom: "", dateEncaissement: "", reference: "", observations: "" });
                setRefetch(p => !p);
            })
            .catch((err) => {
                toast.error("Erreur lors de l'enregistrement");
                console.error(err);
            })
            .finally(() => setLoadingSave(false));
    };

    return (
        <div className="row g-3">
            {/* Résumé financier */}
            <div className="col-12">
                <div className="row g-2">
                    <div className="col-md-4">
                        <MontantBadge
                            label="Total encaissé (recours)"
                            montant={totalRecours}
                            color="green"
                            icon={<DollarOutlined style={{ color: "#52c41a" }} />}
                        />
                    </div>
                    <div className="col-md-4">
                        <MontantBadge
                            label="Nombre d'encaissements"
                            montant={null}
                            color="blue"
                            icon={<BankOutlined style={{ color: "#1677ff" }} />}
                        />
                    </div>
                </div>
            </div>

            {/* Encaissements recours */}
            <div className="col-12">
                <Card
                    size="small"
                    title={
                        <span>
                            <BankOutlined className="me-2 text-success" />
                            Encaissements recours
                            <Badge count={encaissements?.length || 0} color="green" className="ms-2" />
                        </span>
                    }
                    extra={
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => setModalOpen(true)}
                        >
                            Nouvel encaissement
                        </Button>
                    }
                >
                    {loadingSinistre ? (
                        <Skeleton active />
                    ) : encaissements?.length === 0 || !encaissements ? (
                        <div className="text-center text-muted py-4">
                            <DollarOutlined style={{ fontSize: 32, opacity: 0.3 }} />
                            <div className="mt-2">Aucun encaissement recours enregistré</div>
                        </div>
                    ) : (
                        <List
                            dataSource={encaissements}
                            renderItem={(item) => (
                                <List.Item
                                    key={item.codeId}
                                    extra={
                                        <Tag color="green" style={{ fontSize: 14, fontWeight: 700 }}>
                                            + {displayNumbers(item.montantGlobal)} FCFA
                                        </Tag>
                                    }
                                >
                                    <List.Item.Meta
                                        avatar={<Avatar icon={<BankOutlined />} style={{ backgroundColor: "#52c41a" }} />}
                                        title={
                                            <Space wrap>
                                                <span>{item.sourceRecours || item.motif || "Recours"}</span>
                                                {item.sourceNomRecours && (
                                                    <Tag color="cyan">{item.sourceNomRecours}</Tag>
                                                )}
                                                <Tag color={
                                                    item.etatDecaissement === "ENCAISSE" ? "green" :
                                                    item.etatDecaissement === "VALIDE" ? "blue" : "orange"
                                                }>
                                                    {item.etatDecaissement}
                                                </Tag>
                                            </Space>
                                        }
                                        description={
                                            <Space split="·" size="small">
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    <ClockCircleOutlined className="me-1" />
                                                    {frenchDate(item.dateReglement || item.createdAt)}
                                                </Text>
                                                {item.referenceRecours && (
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        Réf: {item.referenceRecours}
                                                    </Text>
                                                )}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    )}
                </Card>
            </div>

            {/* Évaluations recours */}
            {recoursEvaluations?.length > 0 && (
                <div className="col-12">
                    <Card
                        size="small"
                        title={<span><FileTextOutlined className="me-2 text-primary" />Évaluations recours</span>}
                    >
                        <List
                            dataSource={recoursEvaluations}
                            renderItem={(item) => (
                                <List.Item key={item.codeId}>
                                    <List.Item.Meta
                                        title={
                                            <Space>
                                                <span>Provision recours : <b>{displayNumbers(item.nouvelleProvision)} FCFA</b></span>
                                                <Tag color="purple">{item.motifRevision}</Tag>
                                            </Space>
                                        }
                                        description={
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {frenchDate(item.createdAt)} · {item.reviseur?.nom || "—"}
                                            </Text>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </div>
            )}

            {/* Modal nouvel encaissement */}
            <Modal
                title={<span><BankOutlined className="me-2" />Nouvel encaissement recours</span>}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={handleSave}
                okText="Enregistrer"
                confirmLoading={loadingSave}
                destroyOnClose
            >
                <div className="row g-2 mt-1">
                    <div className="col-12">
                        <label className="form-label fw-semibold">Source <span className="text-danger">*</span></label>
                        <Select
                            style={{ width: "100%" }}
                            options={SOURCE_RECOURS}
                            value={form.source}
                            onChange={(v) => setForm(p => ({ ...p, source: v }))}
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-semibold">Nom de la source</label>
                        <Input
                            placeholder="Ex: SUNU ASSURANCES, Bureau National CTPV..."
                            value={form.sourceNom}
                            onChange={(e) => setForm(p => ({ ...p, sourceNom: e.target.value }))}
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-semibold">Montant (FCFA) <span className="text-danger">*</span></label>
                        <Input
                            type="number"
                            min={0}
                            value={form.montant}
                            onChange={(e) => setForm(p => ({ ...p, montant: e.target.value }))}
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-semibold">Date d'encaissement</label>
                        <Input
                            type="date"
                            value={form.dateEncaissement}
                            onChange={(e) => setForm(p => ({ ...p, dateEncaissement: e.target.value }))}
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-semibold">Référence / N° bordereau</label>
                        <Input
                            value={form.reference}
                            onChange={(e) => setForm(p => ({ ...p, reference: e.target.value }))}
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-semibold">Observations</label>
                        <TextArea
                            rows={3}
                            value={form.observations}
                            onChange={(e) => setForm(p => ({ ...p, observations: e.target.value }))}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

// ─── Onglet Observations ───────────────────────────────────────────────────────

const ObservationItem = ({ obs }) => {
    const auteur = obs?.auteur;
    const mentionnes = obs?.utilisateursMentionnes || [];
    return (
        <div
            className="mb-3 p-3"
            style={{ borderRadius: 8, background: "#fafafa", border: "1px solid #f0f0f0" }}
        >
            <div className="d-flex align-items-start gap-2">
                <Avatar
                    style={{ backgroundColor: "#1677ff", flexShrink: 0 }}
                    size={36}
                >
                    {initials(auteur?.nom, auteur?.prenom)}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <Text strong style={{ fontSize: 13 }}>
                            {[auteur?.nom, auteur?.prenom].filter(Boolean).join(" ") || "Utilisateur"}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            <ClockCircleOutlined className="me-1" />
                            {frenchDate(obs?.createdAt)}
                        </Text>
                        {mentionnes.length > 0 && (
                            <Space size={4}>
                                {mentionnes.map((u) => (
                                    <Tag key={u.codeId} color="geekblue" style={{ fontSize: 11 }}>
                                        @{u.nom} {u.prenom}
                                    </Tag>
                                ))}
                            </Space>
                        )}
                    </div>
                    <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                        {obs?.contenu}
                    </div>
                    {obs?.fichierJoint && (
                        <div className="mt-1">
                            <Tag icon={<PaperClipOutlined />} color="default">
                                {obs.fichierJoint}
                            </Tag>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TabObservations = ({ sinistre, setRefetch }) => {
    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [contenu, setContenu] = useState("");
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [mentionSearch, setMentionSearch] = useState("");
    const [mentionnes, setMentionnes] = useState([]); // [{codeId, nom, prenom}]
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const textareaRef = useRef(null);

    // Chargement des observations
    useEffect(() => {
        if (!sinistre?.codeId) return;
        setLoading(true);
        axiosInstance
            .get(`/api/sinistre/${sinistre.codeId}/observations`)
            .then((res) => setObservations(res.data || []))
            .catch(() => setObservations([]))
            .finally(() => setLoading(false));
    }, [sinistre?.codeId]);

    // Chargement des utilisateurs pour mention
    useEffect(() => {
        axiosInstance
            .get("/api/utilisateur")
            .then((res) => setUtilisateurs(res.data?.content || res.data || []))
            .catch(() => {});
    }, []);

    const utilisateursFiltres = utilisateurs.filter((u) => {
        const q = mentionSearch.toLowerCase();
        return (
            u.nom?.toLowerCase().includes(q) ||
            u.prenom?.toLowerCase().includes(q) ||
            u.userName?.toLowerCase().includes(q)
        );
    });

    const handleTextChange = (e) => {
        const val = e.target.value;
        setContenu(val);
        const atIdx = val.lastIndexOf("@");
        if (atIdx !== -1 && atIdx === val.length - 1) {
            setMentionSearch("");
            setShowMentionDropdown(true);
        } else if (atIdx !== -1 && !val.slice(atIdx + 1).includes(" ")) {
            setMentionSearch(val.slice(atIdx + 1));
            setShowMentionDropdown(true);
        } else {
            setShowMentionDropdown(false);
        }
    };

    const selectMention = (user) => {
        const atIdx = contenu.lastIndexOf("@");
        const before = contenu.slice(0, atIdx);
        const fullName = `${user.nom} ${user.prenom || ""}`.trim();
        setContenu(`${before}@${fullName} `);
        setMentionnes((prev) =>
            prev.find((u) => u.codeId === user.codeId) ? prev : [...prev, user]
        );
        setShowMentionDropdown(false);
        textareaRef.current?.focus();
    };

    const handleSubmit = () => {
        if (!contenu.trim()) return;
        setSubmitting(true);
        axiosInstance
            .post(`/api/sinistre/${sinistre.codeId}/observations`, {
                contenu: contenu.trim(),
                auteur: { codeId: LOGGED_USER?.codeId },
                utilisateursMentionnes: mentionnes.map((u) => ({ codeId: u.codeId })),
            })
            .then((res) => {
                setObservations((prev) => [res.data, ...prev]);
                setContenu("");
                setMentionnes([]);
                toast.success("Observation enregistrée");
                setRefetch((p) => !p);
            })
            .catch(() => toast.error("Erreur lors de l'enregistrement"))
            .finally(() => setSubmitting(false));
    };

    return (
        <div className="row g-3">
            {/* Zone de saisie */}
            <div className="col-12">
                <Card
                    size="small"
                    title={<span><MessageOutlined className="me-2 text-primary" />Nouvelle observation</span>}
                >
                    <div className="d-flex gap-2 align-items-start">
                        <Avatar style={{ backgroundColor: "#1677ff", flexShrink: 0 }} size={36}>
                            {initials(LOGGED_USER?.nom, LOGGED_USER?.prenom)}
                        </Avatar>
                        <div style={{ flex: 1, position: "relative" }}>
                            <TextArea
                                ref={textareaRef}
                                rows={3}
                                placeholder='Saisissez votre observation... Utilisez @ pour mentionner un utilisateur'
                                value={contenu}
                                onChange={handleTextChange}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") setShowMentionDropdown(false);
                                    if (e.key === "Enter" && e.ctrlKey) handleSubmit();
                                }}
                                style={{ resize: "vertical" }}
                            />
                            {/* Dropdown mention */}
                            {showMentionDropdown && utilisateursFiltres.length > 0 && (
                                <div
                                    style={{
                                        position: "absolute",
                                        zIndex: 1000,
                                        background: "#fff",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 6,
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                        maxHeight: 180,
                                        overflowY: "auto",
                                        width: 240,
                                        bottom: "100%",
                                        left: 0,
                                    }}
                                >
                                    {utilisateursFiltres.slice(0, 8).map((u) => (
                                        <div
                                            key={u.codeId}
                                            className="d-flex align-items-center gap-2 px-3 py-2"
                                            style={{ cursor: "pointer" }}
                                            onMouseDown={() => selectMention(u)}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                        >
                                            <Avatar size={24} style={{ backgroundColor: "#1677ff", fontSize: 10 }}>
                                                {initials(u.nom, u.prenom)}
                                            </Avatar>
                                            <div>
                                                <div style={{ fontSize: 12, fontWeight: 600 }}>
                                                    {u.nom} {u.prenom}
                                                </div>
                                                <div style={{ fontSize: 10, color: "#888" }}>{u.userName}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Mentions sélectionnées */}
                            {mentionnes.length > 0 && (
                                <div className="mt-1 d-flex flex-wrap gap-1">
                                    {mentionnes.map((u) => (
                                        <Tag
                                            key={u.codeId}
                                            closable
                                            color="geekblue"
                                            style={{ fontSize: 11 }}
                                            onClose={() =>
                                                setMentionnes((prev) =>
                                                    prev.filter((x) => x.codeId !== u.codeId)
                                                )
                                            }
                                        >
                                            @{u.nom} {u.prenom}
                                        </Tag>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Ctrl+Entrée pour envoyer · @ pour mentionner
                        </Text>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={submitting}
                            disabled={!contenu.trim()}
                            onClick={handleSubmit}
                        >
                            Enregistrer
                        </Button>
                    </div>
                </Card>
            </div>

            {/* Historique des observations */}
            <div className="col-12">
                <Card
                    size="small"
                    title={
                        <span>
                            <CommentOutlined className="me-2 text-secondary" />
                            Historique des observations
                            <Badge count={observations.length} color="blue" className="ms-2" />
                        </span>
                    }
                >
                    {loading ? (
                        <Skeleton active paragraph={{ rows: 4 }} />
                    ) : observations.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            <CommentOutlined style={{ fontSize: 32, opacity: 0.3 }} />
                            <div className="mt-2">Aucune observation sur ce dossier</div>
                        </div>
                    ) : (
                        <div style={{ maxHeight: 600, overflowY: "auto" }}>
                            {observations.map((obs) => (
                                <ObservationItem key={obs.codeId || obs.id} obs={obs} />
                            ))}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

// ─── Navigation latérale ───────────────────────────────────────────────────────

const NAV_ITEMS = [
    { key: "infos",        label: "Informations",    icon: <InfoCircleOutlined /> },
    { key: "traitements",  label: "Traitements",     icon: <FileTextOutlined /> },
    { key: "recours",      label: "Recours",         icon: <BankOutlined /> },
    { key: "observations", label: "Observations",    icon: <CommentOutlined /> },
];

const SideNav = ({ active, onChange, counts }) => (
    <div className="card" style={{ minWidth: 180 }}>
        <div className="card-body p-2">
            <div className="nav flex-column nav-pills gap-1">
                {NAV_ITEMS.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => onChange(item.key)}
                        className={`nav-link text-start d-flex align-items-center gap-2 ${active === item.key ? "active" : ""}`}
                        style={{ borderRadius: 6 }}
                    >
                        <span style={{ fontSize: 15 }}>{item.icon}</span>
                        <span style={{ fontSize: 13 }}>{item.label}</span>
                        {counts?.[item.key] > 0 && (
                            <Badge count={counts[item.key]} color={item.key === "observations" ? "blue" : "green"} size="small" className="ms-auto" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    </div>
);

// ─── Composant principal ───────────────────────────────────────────────────────

const DetailsSinistreV2 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const codeId = location?.state?.codeId;

    const [sinistre, setSinistre] = useState(null);
    const [activeTab, setActiveTab] = useState("infos");
    const [loadingSinistre, setLoadingSinistre] = useState(false);
    const [montant, setMontant] = useState(0);
    const [montantPaye, setMontantPaye] = useState(0);
    const [decaissements, setDecaissements] = useState([]);
    const [encaissements, setEncaissements] = useState([]);
    const [recoursEvaluations, setRecoursEvaluations] = useState([]);
    const [evaluations, setEvaluations] = useState([]);
    const [refetch, setRefetch] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    useEffect(() => {
        if (!codeId) return;
        setLoadingSinistre(true);
        axiosInstance
            .get(`/api/sinistre/codeId/${codeId}`)
            .then((res) => {
                const data = res.data;
                setSinistre(data);
                setEvaluations(data?.evaluations?.filter((d) => !d.encaissement && !d.recours) || []);
                setRecoursEvaluations(data?.evaluations?.filter((d) => d.recours === true) || []);
                setDecaissements(
                    (data?.decaissements || [])
                        .filter((d) => !d.encaissement)
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                );
                setEncaissements(
                    (data?.decaissements || [])
                        .filter((d) => d.encaissement === true)
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                );
                let m = 0, mt = 0;
                (data?.decaissements || [])
                    .filter((d) => !d.encaissement && ["ENCAISSE", "VALIDE", "EN_ATTENTE_D_ENCAISSEMENT"].includes(d.etatDecaissement))
                    .forEach((d) => {
                        m += parseFloat(d.montantGlobal) || 0;
                        if (d.etatDecaissement === "ENCAISSE") mt += parseFloat(d.montantGlobal) || 0;
                    });
                setMontant(m);
                setMontantPaye(mt);
            })
            .catch(console.error)
            .finally(() => setLoadingSinistre(false));
    }, [codeId, refetch]);

    useEffect(() => {
        if (searchValue?.length > 2) {
            searchSinistre(searchValue)
                .then((res) => setSearchResults(res.data.content))
                .catch(console.error);
        }
    }, [searchValue]);

    const renderSearchResults = (results) => (
        <div className="px-3 py-2">
            {results?.map((item, index) => (
                <div
                    key={index}
                    onClick={() => navigate("/details-sinistre-v2", { state: { codeId: item.codeId } })}
                    className="dropdown-item d-flex align-items-center gap-2 py-1"
                    style={{ cursor: "pointer" }}
                >
                    <span className="text-truncate">
                        {[
                            item?.numeroSinistreM || item?.numeroSinistreP || item?.numSinistre,
                            item?.numeroPoliceM || item?.numeroPoliceP,
                            frenchDate(item?.dateSinistre),
                            item?.immatriculation,
                            item?.nom,
                            item?.prenom || "",
                        ].map((text, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && " - "}
                                {highlightMatch(text?.toString(), searchValue)}
                            </React.Fragment>
                        ))}
                    </span>
                </div>
            ))}
        </div>
    );

    const renderTab = () => {
        switch (activeTab) {
            case "infos":
                return (
                    <TabInfos
                        sinistre={sinistre}
                        montant={montant}
                        montantPaye={montantPaye}
                    />
                );
            case "traitements":
                return (
                    <div className="alert alert-info">
                        Le contenu des traitements est affiché dans l&apos;onglet Traitements de l&apos;ancienne version.
                        Ce composant sera enrichi progressivement.
                    </div>
                );
            case "recours":
                return (
                    <TabRecours
                        sinistre={sinistre}
                        encaissements={encaissements}
                        recoursEvaluations={recoursEvaluations}
                        setRefetch={setRefetch}
                        loadingSinistre={loadingSinistre}
                    />
                );
            case "observations":
                return (
                    <TabObservations
                        sinistre={sinistre}
                        setRefetch={setRefetch}
                    />
                );
            default:
                return null;
        }
    };

    const navCounts = {
        recours: encaissements?.length || 0,
        observations: 0,
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu="SINISTRES" />
                <div className="layout-page">
                    <NavBar
                        filteredResults={searchResults}
                        setFilteredResults={setSearchResults}
                        renderSearchResults={renderSearchResults}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                    />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {/* Bandeau header */}
                            <SinistreHeaderBand sinistre={sinistre} loading={loadingSinistre} />

                            {/* Layout deux colonnes : nav gauche + contenu */}
                            <div className="d-flex gap-3 align-items-start">
                                {/* Navigation latérale */}
                                <div style={{ flexShrink: 0 }}>
                                    <SideNav
                                        active={activeTab}
                                        onChange={setActiveTab}
                                        counts={navCounts}
                                    />
                                </div>

                                {/* Contenu principal */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {loadingSinistre ? (
                                        <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
                                    ) : (
                                        renderTab()
                                    )}
                                </div>
                            </div>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default DetailsSinistreV2;
